import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ActionMeta } from "react-select"
import Text from "../../../../components/Text";
import Column from "../../../../components/Column";
import Table from "../../../../components/Table";
import { styles } from "../../../../styles/themes/style";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import { useHubspotHandlers } from "../../../../services/actions/hubspot/useHubspotHandlers";
import Row from "../../../../components/Row";
import { calculateColumnWidth } from "../../../../utils";
import useMediaQuery from "../../../../components/useMediaQuery";
import Select from "../../../../components/select/Select";
import { AppRoutes } from "../../../../interfaces/store/appStore";
import { useNavigate } from "react-router-dom";
import SvgFragment from "../../../../fragments/SvgFragment";
import Button from "../../../../components/Button";
import TooltipWrapper from "../../../../components/TooltipWrapper";

interface IHubspotFieldMappingProps { }

const HubspotFieldMapping: FunctionComponent<IHubspotFieldMappingProps> = () => {
    const navigate = useNavigate()
    const tableRef = useRef<HTMLDivElement>(null)
    const hasValidatedRef = useRef<boolean>(false)
    const isSmall = useMediaQuery("(max-width: 768px)")

    const { showSuccessToast, showErrorToast } = useAppHandlers()
    const { getContactPropertiesHandler, saveFieldMappingsHandler, getOAuthStatusHandler } = useHubspotHandlers()

    const [leadDeltaFields, setLeadDeltaFields] = useState<any[]>([])
    const [hubspotProperties, setHubspotProperties] = useState<any[]>([])
    const [selectedMappings, setSelectedMappings] = useState<any>({})
    const [loading, setLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [fieldErrors, setFieldErrors] = useState<any>({})
    const [isConnected, setIsConnected] = useState<boolean>(false)
    const [checkingConnection, setCheckingConnection] = useState<boolean>(true)
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Required mapping: linkedinUrl must always map to hs_linkedin
    const REQUIRED_LINKEDIN_MAPPING = {
        ldField: 'linkedinUrl',
        hubspotProperty: 'hs_linkedin_url'
    }

    const [columns, setColumns] = useState([
        { header: 'LeadDelta Field', key: ['ldField'], width: '200px', show: true, showSmall: true },
        { header: 'Description', key: ['description'], show: true, showSmall: true },
        { header: 'HubSpot Property', key: ['mapping'], width: '300px', show: true, showSmall: true }
    ])

    /**
     * Check if HubSpot field type is compatible with LeadDelta field type
     */
    const isTypeCompatible = (ldFieldType: string, hubspotType: string, hubspotFieldType?: string): boolean => {
        // Exact match
        if (ldFieldType === hubspotType) return true

        // Type compatibility mappings
        const typeMap: any = {
            'string': ['string', 'text'],
            'email': ['string', 'text', 'email'],
            'phoneNumber': ['string', 'text', 'phone'],
            'url': ['string', 'text', 'url'],
            'text': ['string', 'text'],
            'number': ['number'],
            'date': ['date', 'datetime'],
            'timestamp': ['date', 'datetime'],
            'boolean': ['bool', 'boolean'],
            'array': ['enumeration', 'string', 'text'], // Arrays can map to multi-select enumerations or be joined as strings
        }

        const compatibleTypes = typeMap[ldFieldType] || []
        
        // Check if HubSpot type is in compatible types
        if (compatibleTypes.includes(hubspotType)) return true
        
        // Special case: enumeration can sometimes work for strings
        if (ldFieldType === 'string' && hubspotType === 'enumeration') return true
        
        return false
    }

    useEffect(() => {
        checkHubSpotConnection()
    }, [])

    useEffect(() => {
        if (isConnected) {
            fetchHubSpotProperties()
        }
    }, [isConnected])

    const checkHubSpotConnection = async () => {
        try {
            setCheckingConnection(true)
            const status = await getOAuthStatusHandler()
            
            if (status?.connected) {
                setIsConnected(true)
            } else {
                setIsConnected(false)
                setLoading(false)
            }
        } catch (error) {
            console.error('Error checking HubSpot connection:', error)
            setIsConnected(false)
            setLoading(false)
        } finally {
            setCheckingConnection(false)
        }
    }

    useEffect(() => {
        const offsetWidth = tableRef.current?.offsetWidth
        const adjustedColumns = calculateColumnWidth(offsetWidth, columns, isSmall)
        setColumns(adjustedColumns)
    }, [isSmall])

    // Validate existing mappings when data is loaded (only once)
    useEffect(() => {
        if (!hasValidatedRef.current && leadDeltaFields.length > 0 && hubspotProperties.length > 0) {
            const currentMappings = selectedMappings
            if (Object.keys(currentMappings).length === 0) return
            
            hasValidatedRef.current = true
            const errors: any = {}
            
            Object.entries(currentMappings).forEach(([ldFieldValue, hubspotPropName]: [string, any]) => {
                if (!hubspotPropName) return // Skip empty mappings
                
                const ldField = leadDeltaFields.find(f => f.value === ldFieldValue)
                const hubspotProp = hubspotProperties.find(p => p.name === hubspotPropName)
                
                if (ldField && hubspotProp) {
                    const isCompatible = isTypeCompatible(ldField.type, hubspotProp.type, hubspotProp.fieldType)
                    if (!isCompatible) {
                        // Show error but keep the mapping as is
                        errors[ldFieldValue] = `Type mismatch: "${ldField.label}" has type "${ldField.type}" but "${hubspotProp.label}" has type "${hubspotProp.type}"`
                    }
                }
            })
            
            if (Object.keys(errors).length > 0) {
                setFieldErrors(errors)
                showErrorToast({ 
                    message: `Found ${Object.keys(errors).length} type mismatch(es). Please review and correct the mappings.` 
                })
            }
        }
    }, [leadDeltaFields, hubspotProperties])

    const fetchHubSpotProperties = async () => {
        try {
            setLoading(true)
            hasValidatedRef.current = false // Reset validation flag
            const result = await getContactPropertiesHandler()
            
            if (result) {
                setLeadDeltaFields(result.leadDeltaFields || [])
                setHubspotProperties(result.hubspotFields || [])
                const userSettings = result.userSettings || {}
                
                // Force linkedinUrl to always map to hs_linkedin
                const enforcedMappings = {
                    ...userSettings,
                    [REQUIRED_LINKEDIN_MAPPING.ldField]: REQUIRED_LINKEDIN_MAPPING.hubspotProperty
                }
                
                setSelectedMappings(enforcedMappings)
            }

        } catch (error) {
            console.error('Error fetching HubSpot properties:', error)
            showErrorToast({ message: 'Failed to load HubSpot properties' })
        } finally {
            setLoading(false)
        }
    }

    const clearPendingSave = () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current)
            saveTimeoutRef.current = null
        }
    }

    const persistMappings = useCallback(async (mappings: Record<string, string | null>) => {
        try {
            setIsSaving(true)
            
            // Ensure linkedinUrl is always mapped to hs_linkedin
            const mappingsToSave = {
                ...mappings,
                [REQUIRED_LINKEDIN_MAPPING.ldField]: REQUIRED_LINKEDIN_MAPPING.hubspotProperty
            }
            
            const result = await saveFieldMappingsHandler(mappingsToSave)

            if (result) {
                showSuccessToast({ message: 'Field mappings saved successfully!' })
            }
        } catch (error) {
            console.error('Error saving mappings:', error)
            showErrorToast({ message: 'Failed to save mappings' })
        } finally {
            setIsSaving(false)
        }
    }, [saveFieldMappingsHandler, showSuccessToast, showErrorToast])

    const scheduleAutoSave = useCallback((mappings: Record<string, string | null>, errors: Record<string, string | undefined>, shouldSave: boolean) => {
        clearPendingSave()

        if (!shouldSave || Object.keys(errors).length > 0) {
            return
        }

        saveTimeoutRef.current = setTimeout(() => {
            persistMappings(mappings)
        }, 500)
    }, [persistMappings])

    const handleMappingChange = (ldField: string, hubspotProperty: string | null, actionMeta?: ActionMeta<any>) => {
        // Prevent changing the required linkedinUrl mapping
        if (ldField === REQUIRED_LINKEDIN_MAPPING.ldField) {
            showErrorToast({
                message: 'LinkedIn URL must always be mapped to hs_linkedin. This is crucial for recognizing and mapping contacts to HubSpot to prevent duplicates.'
            })
            return
        }
        
        const ldFieldObj = leadDeltaFields.find(f => f.value === ldField)
        const previousValue = selectedMappings[ldField] ?? null
        const newValue = hubspotProperty ?? null

        const isUserAction = !actionMeta || !['set-value', 'initial-input-focus', 'input-change'].includes(actionMeta.action)
        const isNotesField = ldFieldObj?.isNotesField || ldField === 'notes'
        const isTagsField = ldFieldObj?.isTagsField || ldField === 'tags'
        const isCompanyField = ldFieldObj?.isCompanyField || ldField === 'company'

        if (!ldFieldObj) {
            const updatedMappings = {
                ...selectedMappings,
                [ldField]: newValue
            }

            const updatedErrors = { ...fieldErrors }
            if (!newValue) {
                delete updatedErrors[ldField]
            }

            setSelectedMappings(updatedMappings)
            setFieldErrors(updatedErrors)
            scheduleAutoSave(updatedMappings, updatedErrors, isUserAction)
            return
        }

        if (previousValue === newValue && actionMeta?.action !== 'clear') {
            return
        }

        const updatedMappings: Record<string, string | null> = {
            ...selectedMappings,
            [ldField]: newValue
        }

        const updatedErrors: Record<string, string | undefined> = { ...fieldErrors }

        if (!newValue) {
            delete updatedErrors[ldField]
            setSelectedMappings(updatedMappings)
            setFieldErrors(updatedErrors)
            scheduleAutoSave(updatedMappings, updatedErrors, isUserAction)
            return
        }

        // For notes field, skip validation and just save
        if (isNotesField && newValue === 'notes') {
            delete updatedErrors[ldField]
            setSelectedMappings(updatedMappings)
            setFieldErrors(updatedErrors)
            scheduleAutoSave(updatedMappings, updatedErrors, isUserAction)
            return
        }

        // For tags field, skip validation and just save
        if (isTagsField && newValue === 'tags') {
            delete updatedErrors[ldField]
            setSelectedMappings(updatedMappings)
            setFieldErrors(updatedErrors)
            scheduleAutoSave(updatedMappings, updatedErrors, isUserAction)
            return
        }

        // For company field, skip validation and just save
        if (isCompanyField && newValue === 'company') {
            delete updatedErrors[ldField]
            setSelectedMappings(updatedMappings)
            setFieldErrors(updatedErrors)
            scheduleAutoSave(updatedMappings, updatedErrors, isUserAction)
            return
        }

        const selectedHubspotProp = hubspotProperties.find(p => p.name === newValue)

        if (!selectedHubspotProp) {
            delete updatedErrors[ldField]
            setSelectedMappings(updatedMappings)
            setFieldErrors(updatedErrors)
            scheduleAutoSave(updatedMappings, updatedErrors, isUserAction)
            return
        }

        const duplicateEntry = Object.entries(selectedMappings).find(
            ([key, value]) => key !== ldField && value === newValue
        )

        if (duplicateEntry) {
            const [conflictingFieldKey] = duplicateEntry
            const conflictingField = leadDeltaFields.find(f => f.value === conflictingFieldKey)
            const conflictLabel = conflictingField?.label || 'one field'

            updatedErrors[ldField] = `HubSpot property "${selectedHubspotProp.label}" is already mapped to "${conflictLabel}".`

            setSelectedMappings(updatedMappings)
            setFieldErrors(updatedErrors)
            clearPendingSave()
            showErrorToast({
                message: `HubSpot property "${selectedHubspotProp.label}" is already mapped to "${conflictLabel}".`
            })
            return
        }

        const isCompatible = isTypeCompatible(ldFieldObj.type, selectedHubspotProp.type, selectedHubspotProp.fieldType)

        if (!isCompatible) {
            updatedErrors[ldField] = `Type mismatch: "${ldFieldObj.label}" has type "${ldFieldObj.type}" but "${selectedHubspotProp.label}" has type "${selectedHubspotProp.type}"`
            setSelectedMappings(updatedMappings)
            setFieldErrors(updatedErrors)
            clearPendingSave()
            showErrorToast({ 
                message: `Type mismatch: "${ldFieldObj.label}" (${ldFieldObj.type}) cannot be mapped to "${selectedHubspotProp.label}" (${selectedHubspotProp.type})`
            })
            return
        }

        delete updatedErrors[ldField]
        setSelectedMappings(updatedMappings)
        setFieldErrors(updatedErrors)
        scheduleAutoSave(updatedMappings, updatedErrors, isUserAction)
    }

    useEffect(() => {
        return () => {
            clearPendingSave()
        }
    }, [])

    const tableData = leadDeltaFields?.map(ldField => {
        const isLinkedInUrlField = ldField.value === REQUIRED_LINKEDIN_MAPPING.ldField
        const isNotesField = ldField.isNotesField || ldField.value === 'notes'
        const isTagsField = ldField.isTagsField || ldField.value === 'tags'
        const isCompanyField = ldField.isCompanyField || ldField.value === 'company'
        
        // For notes/tags/company fields, use custom options instead of HubSpot properties
        const selectOptions = isNotesField
            ? [
                { value: null, label: '-- Do not map --' },
                { value: 'notes', label: 'Notes' }
              ]
            : isTagsField
            ? [
                { value: null, label: '-- Do not map --' },
                { value: 'tags', label: 'Tags' }
              ]
            : isCompanyField
            ? [
                { value: null, label: '-- Do not map --' },
                { value: 'company', label: 'Company' }
              ]
            : [
                { value: null, label: '-- Do not map --' },
                ...hubspotProperties.map(prop => ({
                    value: prop.name,
                    label: prop.label
                }))
              ]
        
        // For notes/tags/company fields, find the value in custom options
        const selectValue = isNotesField
            ? (selectedMappings[ldField.value] === 'notes' ? { value: 'notes', label: 'Notes' } : null)
            : isTagsField
            ? (selectedMappings[ldField.value] === 'tags' ? { value: 'tags', label: 'Tags' } : null)
            : isCompanyField
            ? (selectedMappings[ldField.value] === 'company' ? { value: 'company', label: 'Company' } : null)
            : (hubspotProperties.find(p => p.name === selectedMappings[ldField.value]) ? {
                value: selectedMappings[ldField.value],
                label: hubspotProperties.find(p => p.name === selectedMappings[ldField.value])?.label
              } : null)
        
        return {
            ldField: <Text $bold>{ldField.label}</Text>,
            description: <Text $lighter>{ldField.description}</Text>,
            mapping: (
                <MappingWrapper>
                    <TooltipWrapper tooltipText={
                        isLinkedInUrlField 
                            ? "Required for the correct contact recognition" 
                            : isNotesField
                                ? "Map to Notes to sync connection notes to HubSpot"
                                : isTagsField
                                    ? "Map to Tags to sync connection tags to HubSpot"
                                    : isCompanyField
                                        ? "Map to Company to create/update company in HubSpot and associate with contact"
                                        : "Please choose a Hubspot Contact Property"
                    }>
                        <SelectWrapper>
                            <Select
                                value={selectValue}
                                options={selectOptions}
                                onChange={(option: any, actionMeta: ActionMeta<any>) => handleMappingChange(ldField.value, option?.value ?? null, actionMeta)}
                                placeholder={isNotesField ? "Select Notes mapping" : isTagsField ? "Select Tags mapping" : isCompanyField ? "Select Company mapping" : "Select HubSpot property"}
                                isClearable={!isLinkedInUrlField}
                                isDisabled={isLinkedInUrlField}
                            />
                        </SelectWrapper>
                    </TooltipWrapper>
                    {fieldErrors[ldField.value] && !isLinkedInUrlField && (
                        <ErrorText>{fieldErrors[ldField.value]}</ErrorText>
                    )}
                </MappingWrapper>
            )
        }
    })

    // Show not connected message if HubSpot is not connected
    if (!checkingConnection && !isConnected) {
        return (
            <Container>
                <NotConnectedWrapper>
                    <Column gap='24px' alignItems>
                        <StyledSvgFragment type='hubspot' />
                        <Column gap='12px' alignItems>
                            <Text $heading4>HubSpot Not Connected</Text>
                            <Text $lighter style={{ textAlign: 'center', maxWidth: '400px' }}>
                                Please connect your HubSpot account before configuring field mappings.
                            </Text>
                        </Column>
                        <Button 
                            $type='blue' 
                            $bigButton
                            onClickHandler={() => navigate(AppRoutes.INTEGRATIONS + '/hubspot')}
                        >
                            Connect HubSpot
                        </Button>
                    </Column>
                </NotConnectedWrapper>
            </Container>
        )
    }

    return (
        <Container>
            <Header>
                <Row gap='12px' alignItems>
                    <Column gap='8px' style={{ flex: 1 }}>
                        <Text $heading4>HubSpot Field Mapping</Text>
                        <Text $lighter>
                            Map your LeadDelta fields to HubSpot contact properties. This determines how your contact data will be synced to HubSpot.
                        </Text>
                    </Column>
                </Row>
            </Header>

            <Content ref={tableRef}>
                <Table
                    pageLoading={loading ? 10 : undefined}
                    columns={columns}
                    data={tableData || []}
                    noDataPage={
                        <Column gap='12px' alignItems>
                            <Text $heading4>No Field Mappings</Text>
                            <Text $lighter>Unable to load HubSpot properties.</Text>
                        </Column>
                    }
                />
            </Content>
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
    height: 100%;
    max-height: calc(100vh - 180px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
`

const Header = styled.div`
    padding: 24px;
    padding-bottom: 16px;
    flex-shrink: 0;
`

const Content = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 24px 24px 24px;
`

const NotConnectedWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: 400px;
    padding: 48px 24px;
`

const StyledSvgFragment = styled(SvgFragment)`
    width: 80px;
    height: 80px;
    
    svg {
        width: 80px;
        height: 80px;
    }
`

const SelectWrapper = styled.div`
    width: 100%;
    position: relative;
`

const MappingWrapper = styled(Column)`
    width: 100%;
    gap: 4px;
`

const ErrorText = styled(Text)`
    color: ${styles.colors.red600};
    font-size: 12px;
    margin-top: 4px;
`

export default HubspotFieldMapping

